/*
 * (c) Copyright, 2024, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useState, useEffect, useContext } from 'react'
import { useServiceContext } from '../../../../root'
import { BlueInfoBox } from '../../../../components/shared/BlueInfoBox'
import OverviewTable from './OverviewTable'
import { RcmTableDataWrapper } from '../../../../components/rcm-table/RcmTableDataWrapper'
import { RcmTableDataContext } from '../../../../components/rcm-table/RcmTableDataContext'
import TableFilter from './TableFilter'
import type { StudyDocumentsFilterData } from '../types'

interface StudyDocumentsProps {
  filter: StudyDocumentsFilterData
}

// Inner component that has access to RcmTableDataContext
const StudyDocumentsContent: React.FC<{
  filter: StudyDocumentsFilterData
  showTable: boolean
  showMilestone: boolean
  toggleTableVisibility: (data: boolean) => void
  setMilestoneVisibility: (data: boolean) => void
}> = ({ filter, showTable, showMilestone, toggleTableVisibility, setMilestoneVisibility }) => {
  return (
    <>
      <TableFilter
        filter={filter}
        changeTableVisibility={(data) => toggleTableVisibility(data)}
        setMilestoneVisibility={(data) => setMilestoneVisibility(data)}
      />
      {showTable ? <OverviewTable showMilestone={showMilestone} /> : <BlueInfoBox />}
    </>
  )
}

const StudyDocuments: React.FC<StudyDocumentsProps> = (props) => {
  const context = useServiceContext()
  const [showTable, setShowTable] = useState(true)
  const [showMilestone, setShowMilestone] = useState(false)

  const setMilestoneVisibility = (data: boolean) => {
    setShowMilestone(data)
  }

  const toggleTableVisibility = (data: boolean) => {
    setShowTable(data)
  }

  const getAssembledDataForPropertyBasedSearch = (forExport: boolean, filterFormData: StudyDocumentsFilterData) => {
    let fsiId: string | number | null = null
    const DTO: any = {}

    // Capitalize first letter of level string
    const capitalizeString = (str: string) => {
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
    }

    DTO.level = filterFormData?.level ? capitalizeString(filterFormData.level) : 'ALL'

    if (filterFormData?.PRODUCT) {
      DTO.product = filterFormData.PRODUCT.fsiPropName
      fsiId = filterFormData.PRODUCT.fsiId
    }
    if (filterFormData.STUDY) {
      DTO.study = filterFormData.STUDY.fsiPropName
      fsiId = filterFormData.STUDY.fsiId
    }
    if (filterFormData.ENVIRONMENT) {
      DTO.environment = filterFormData.ENVIRONMENT.fsiPropName
      fsiId = filterFormData.ENVIRONMENT.fsiId
    }
    if (filterFormData?.COUNTRY && filterFormData.COUNTRY.fsiId !== 'all') {
      DTO.country = filterFormData.COUNTRY.fsiPropName
      fsiId = filterFormData.COUNTRY.fsiId
    }
    if (filterFormData?.SITE && filterFormData.SITE.fsiId !== 'all') {
      DTO.site = filterFormData.SITE.fsiPropName
      fsiId = filterFormData.SITE.fsiId
    }
    if (filterFormData.ZONE) {
      DTO.zone = filterFormData.ZONE.fsiPropName
      fsiId = filterFormData.ZONE.fsiId
    }
    if (filterFormData.SECTION) {
      DTO.section = filterFormData.SECTION.fsiPropName
      fsiId = filterFormData.SECTION.fsiId
    }
    if (filterFormData?.tagIds?.length) {
      DTO.tagIds = filterFormData.tagIds.map((tag: any) => tag.value || tag.id)
    }
    if (filterFormData?.artifacts?.length) {
      DTO.artifacts = filterFormData.artifacts.map((artifact: any) => artifact.label)
    }
    if (filterFormData?.milestone) {
      DTO.milestone = filterFormData.milestone.label
    }
    if (filterFormData?.stage) {
      DTO.stage = filterFormData.stage.label
    }
    if (filterFormData.rule) {
      DTO.rule = filterFormData.rule.label
    }
    DTO.fsiId = fsiId
    DTO.type = filterFormData?.completionType ? filterFormData.completionType : 'ALL'

    return {
      ...DTO,
      ...(forExport ? {} : { userId: context.service.auth.getUserId() })
    }
  }

  const loadData = (
    params: { pageSize: number; pageStart: number; sortBy?: string; sortDesc?: boolean },
    filterData: StudyDocumentsFilterData
  ) => {
    const request = getAssembledDataForPropertyBasedSearch(false, filterData)
    params.sortBy = ''

    return new Promise<{ elements: any[]; totalElements: number }>((resolve, reject) => {
      context.api.etmf.getStudyDocumentsForOverviewPropertyBased(request, params).then(
        (data) => {
          resolve({
            elements:
              data?.elements.map((item: any) => ({
                trial: item?.fsiStudy ? item.fsiStudy : null,
                country: item?.fsiCountry ? item.fsiCountry : null,
                site: item?.fsiSite ? item.fsiSite : null,
                section: item?.fsiSection ? item.fsiSection : null,
                zone: item?.fsiZone ? item.fsiZone : null,
                milestone: item?.fsiMilestone ? item.fsiMilestone : null,
                environment: item?.fsiEnv ? item.fsiEnv : null,
                name: item?.fsiName ? item.fsiName : null,
                id: item?.fsiId ? item.fsiId : null
              })) ?? [],
            totalElements: data?.totalElements ?? 0
          })
        },
        (err) => {
          console.error('[StudyDocuments] API error:', err)
          context.service.toast.error(context.service.filter.translate(err?.data?.message ? err.data.message : 'ERROR'))
          reject(err)
        }
      )
    })
  }

  // Generate a unique key based on filter to force table reload when filter changes
  // const filterKey = JSON.stringify(props?.filter)

  return (
    <RcmTableDataWrapper loadData={loadData} persistenceKey="overviewDocuments">
      <StudyDocumentsContent
        filter={props?.filter}
        showTable={showTable}
        showMilestone={showMilestone}
        toggleTableVisibility={toggleTableVisibility}
        setMilestoneVisibility={setMilestoneVisibility}
      />
    </RcmTableDataWrapper>
  )
}

export default StudyDocuments
